"use client"

import { Image, Text } from "@/elements"
import React, { FC } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { GrabIcon } from "@/icons/outline/GrabIcon"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

const ReorderProjectsModalItemFragment = graphql(`
  fragment ReorderProjectsModalItem on CreativeWorkPortfolioProject {
    __typename
    id
    title
    creatorRolesString
    thumbnailFocalPointX
    thumbnailFocalPointY
    thumbnail240w: thumbnailUrl(width: 240)
  }
`)

interface Props {
  project: FragmentType<typeof ReorderProjectsModalItemFragment> | undefined | null
}

export const ReorderProjectsModalItem: FC<Props> = props => {
  const project = useFragment(ReorderProjectsModalItemFragment, props.project)

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: project?.id! })

  const xPosition = project?.thumbnailFocalPointX ?? 0.5
  const yPosition = project?.thumbnailFocalPointY ?? 0.5

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex cursor-grab flex-row items-center"
      {...attributes}
      {...listeners}>
      <div className="px-4">
        <GrabIcon className="flex flex-shrink-0 stroke-fg-2" />
      </div>
      <div className="relative flex size-[64px] flex-shrink-0 overflow-hidden rounded-sm md:rounded-md">
        <Image
          srcSetUrls={{
            sm: project?.thumbnail240w ?? "",
          }}
          alt={project?.title ?? ""}
          className="h-full w-full object-cover"
          fetchPriority="auto"
          style={{
            objectPosition: `${xPosition * 100}% ${yPosition * 100}%`,
          }}
        />
      </div>
      <div className="pl-4">
        <Text variant="body1" maxLines={1}>
          {project?.title}
        </Text>
        <Text variant="body2" maxLines={1} className="text-fg-2">
          {project?.creatorRolesString}
        </Text>
      </div>
    </div>
  )
}
